/**
 * stepToScad.ts — Converts STEP geometry (via occt-import-js meshes) into
 * editable SCAD code by recognizing primitives from B-rep face analysis.
 *
 * Pipeline: brep_faces → classify faces → assemble primitives → detect holes → emit SCAD
 */

// ── Types ──────────────────────────────────────────────────────────────────────

/** Matches OcctMesh from occt-import-js.d.ts */
interface OcctMesh {
  name: string;
  color?: [number, number, number];
  attributes: {
    position: { array: number[] };
    normal?: { array: number[] };
  };
  index: { array: number[] };
  brep_faces: Array<{
    first: number;
    last: number;
    color: [number, number, number] | null;
  }>;
}

type Vec3 = [number, number, number];

interface ClassifiedFace {
  type: 'plane' | 'cylinder' | 'sphere' | 'cone' | 'unknown';
  triangleIndices: number[];  // indices into the mesh index array
  positions: number[];        // flat xyz vertex positions for this face
  normals: number[];          // flat xyz normals for this face
  // Plane
  normal?: Vec3;
  distance?: number;
  // Cylinder / Cone
  axis?: Vec3;
  axisPoint?: Vec3;
  radius?: number;
  radius2?: number;           // for cone: second radius
  // Sphere
  center?: Vec3;
}

interface RecognizedPrimitive {
  type: 'box' | 'cylinder' | 'cone' | 'sphere' | 'lshape' | 'unknown';
  faces: ClassifiedFace[];
  size?: Vec3;
  position?: Vec3;
  height?: number;
  radius?: number;
  radius2?: number;
  rotation?: Vec3;
  axis?: Vec3;                  // cylinder/cone axis direction (unit vector)
  isHole: boolean;
  unknownFaceCount?: number;    // count of unrecognized faces (for reporting)
  // L-shape specific (difference of outer box - notch box)
  notchSize?: Vec3;
  notchPosition?: Vec3;
}

// ── Vector helpers ─────────────────────────────────────────────────────────────

const ANGLE_TOL = Math.cos(5 * Math.PI / 180);  // ~5° tolerance
const RADIUS_TOL = 0.02;  // 2% relative tolerance for radius consistency

function vecSub(a: Vec3, b: Vec3): Vec3 {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}

function vecAdd(a: Vec3, b: Vec3): Vec3 {
  return [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
}

function vecScale(v: Vec3, s: number): Vec3 {
  return [v[0] * s, v[1] * s, v[2] * s];
}

function vecDot(a: Vec3, b: Vec3): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

function vecCross(a: Vec3, b: Vec3): Vec3 {
  return [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
  ];
}

function vecLen(v: Vec3): number {
  return Math.sqrt(v[0] * v[0] + v[1] * v[1] + v[2] * v[2]);
}

function vecNormalize(v: Vec3): Vec3 {
  const len = vecLen(v);
  if (len < 1e-12) return [0, 0, 0];
  return [v[0] / len, v[1] / len, v[2] / len];
}

// ── Geometry helpers ─────────────────────────────────────────────────────────

/** Return two orthogonal unit vectors perpendicular to the given axis. */
function orthoBasis(axis: Vec3): [Vec3, Vec3] {
  const up: Vec3 = Math.abs(axis[0]) < 0.9 ? [1, 0, 0] : [0, 1, 0];
  const u = vecNormalize(vecCross(axis, up));
  const v = vecCross(axis, u);  // already unit length (axis and u are unit + perpendicular)
  return [u, v];
}

/**
 * Algebraic circle fit (Kåsa method) for 2D points.
 * Returns center (cx, cy) and radius, or null if degenerate.
 */
function fitCircle2D(
  points: Array<[number, number]>,
): { cx: number; cy: number; r: number } | null {
  const n = points.length;
  if (n < 3) return null;

  // Minimize Σ(xi² + yi² + D·xi + E·yi + F)²
  let Sx = 0, Sy = 0, Sxx = 0, Sxy = 0, Syy = 0, Sxz = 0, Syz = 0, Sz = 0;
  for (const [x, y] of points) {
    const z = x * x + y * y;
    Sx += x; Sy += y;
    Sxx += x * x; Sxy += x * y; Syy += y * y;
    Sxz += x * z; Syz += y * z; Sz += z;
  }

  // 3×3 system: [[Sxx,Sxy,Sx],[Sxy,Syy,Sy],[Sx,Sy,n]] · [D,E,F] = [-Sxz,-Syz,-Sz]
  const det =
    Sxx * (Syy * n - Sy * Sy) -
    Sxy * (Sxy * n - Sy * Sx) +
    Sx * (Sxy * Sy - Syy * Sx);
  if (Math.abs(det) < 1e-12) return null;

  const D =
    ((-Sxz) * (Syy * n - Sy * Sy) -
      Sxy * ((-Syz) * n - Sy * (-Sz)) +
      Sx * ((-Syz) * Sy - Syy * (-Sz))) /
    det;
  const E =
    (Sxx * ((-Syz) * n - Sy * (-Sz)) -
      (-Sxz) * (Sxy * n - Sy * Sx) +
      Sx * (Sxy * (-Sz) - (-Syz) * Sx)) /
    det;

  const cx = -D / 2;
  const cy = -E / 2;

  // Compute mean radius from fitted center
  let rSum = 0;
  for (const [x, y] of points) {
    rSum += Math.sqrt((x - cx) * (x - cx) + (y - cy) * (y - cy));
  }
  const r = rSum / n;
  if (r < 1e-8) return null;

  return { cx, cy, r };
}

// ── Rotation helpers ──────────────────────────────────────────────────────────

/** Decompose a 3×3 rotation matrix (columns = local axes) to ZYX Euler angles in degrees. */
function matrixToEulerZYX(cols: [Vec3, Vec3, Vec3]): Vec3 {
  // cols[0] = local X, cols[1] = local Y, cols[2] = local Z
  // Build rotation matrix row-major: R[row][col]
  const R = [
    [cols[0][0], cols[1][0], cols[2][0]],
    [cols[0][1], cols[1][1], cols[2][1]],
    [cols[0][2], cols[1][2], cols[2][2]],
  ];
  const toDeg = 180 / Math.PI;

  // ry = asin(-R[2][0])
  const sy = -R[2][0];
  const cy = Math.sqrt(1 - sy * sy);

  let rx: number, ry: number, rz: number;
  if (cy > 1e-6) {
    ry = Math.asin(sy);
    rx = Math.atan2(R[2][1] / cy, R[2][2] / cy);
    rz = Math.atan2(R[1][0] / cy, R[0][0] / cy);
  } else {
    // Gimbal lock
    ry = sy > 0 ? Math.PI / 2 : -Math.PI / 2;
    rx = Math.atan2(R[0][1], R[1][1]);
    rz = 0;
  }

  return [rx * toDeg, ry * toDeg, rz * toDeg];
}

/** Compute rotation angles to align [0,0,1] with the given axis direction. */
function axisToRotation(axis: Vec3): Vec3 | null {
  const a = vecNormalize(axis);

  // Already aligned with Z+
  if (Math.abs(a[0]) < 1e-6 && Math.abs(a[1]) < 1e-6 && a[2] > 0) return null;

  // Aligned with Z-
  if (Math.abs(a[0]) < 1e-6 && Math.abs(a[1]) < 1e-6 && a[2] < 0) return [180, 0, 0];

  // General case: rotate [0,0,1] to axis via Ry(ry) * Rx(rx)
  // Decomposition: ax = sin(ry)*cos(rx), ay = -sin(rx), az = cos(ry)*cos(rx)
  // Therefore: rx = -asin(ay), ry = atan2(ax, az)  (no division by cos, avoids gimbal lock)
  const rx = -Math.asin(Math.max(-1, Math.min(1, a[1])));
  const ry = Math.atan2(a[0], a[2]);

  const toDeg = 180 / Math.PI;
  const rxd = rx * toDeg;
  const ryd = ry * toDeg;

  // If angles are negligible, no rotation needed
  if (Math.abs(rxd) < 0.01 && Math.abs(ryd) < 0.01) return null;

  return [rxd, ryd, 0];
}

// ── Step 1: Extract face geometry ──────────────────────────────────────────────

function extractFaceGeometry(
  mesh: OcctMesh,
  face: { first: number; last: number },
): { positions: number[]; normals: number[]; triangleIndices: number[] } {
  const posArray = mesh.attributes.position.array;
  const normArray = mesh.attributes.normal?.array;
  const idxArray = mesh.index.array;

  const positions: number[] = [];
  const normals: number[] = [];
  const triangleIndices: number[] = [];

  // face.first and face.last are TRIANGLE indices (not index-array offsets).
  // Multiply by 3 to get the actual index-array offset.
  const idxStart = face.first * 3;
  const idxEnd = face.last * 3 + 2; // inclusive: last triangle's 3rd vertex
  for (let i = idxStart; i <= idxEnd; i++) {
    const vi = idxArray[i];
    triangleIndices.push(vi);
    positions.push(posArray[vi * 3], posArray[vi * 3 + 1], posArray[vi * 3 + 2]);
    if (normArray) {
      normals.push(normArray[vi * 3], normArray[vi * 3 + 1], normArray[vi * 3 + 2]);
    }
  }

  // If no normals provided, compute them from triangles
  if (normals.length === 0) {
    for (let i = 0; i < positions.length; i += 9) {
      const p0: Vec3 = [positions[i], positions[i + 1], positions[i + 2]];
      const p1: Vec3 = [positions[i + 3], positions[i + 4], positions[i + 5]];
      const p2: Vec3 = [positions[i + 6], positions[i + 7], positions[i + 8]];
      const n = vecNormalize(vecCross(vecSub(p1, p0), vecSub(p2, p0)));
      // Same normal for all 3 vertices of this triangle
      normals.push(n[0], n[1], n[2], n[0], n[1], n[2], n[0], n[1], n[2]);
    }
  }

  return { positions, normals, triangleIndices };
}

// ── Step 2: Classify each face ─────────────────────────────────────────────────

function classifyFace(
  positions: number[],
  normals: number[],
  triangleIndices: number[],
): ClassifiedFace {
  const numVerts = positions.length / 3;
  if (numVerts < 3) {
    return { type: 'unknown', triangleIndices, positions, normals };
  }

  // Collect unique vertex normals
  const vertNormals: Vec3[] = [];
  for (let i = 0; i < normals.length; i += 3) {
    vertNormals.push([normals[i], normals[i + 1], normals[i + 2]]);
  }

  // Collect unique vertex positions
  const vertPositions: Vec3[] = [];
  for (let i = 0; i < positions.length; i += 3) {
    vertPositions.push([positions[i], positions[i + 1], positions[i + 2]]);
  }

  // --- Try Plane ---
  const meanNormal = vecNormalize(
    vertNormals.reduce<Vec3>((acc, n) => vecAdd(acc, n), [0, 0, 0]),
  );

  if (vecLen(meanNormal) > 0.5) {
    let allPlanar = true;
    for (const n of vertNormals) {
      const nn = vecNormalize(n);
      if (vecLen(nn) < 0.1) continue; // degenerate normal, skip
      if (Math.abs(vecDot(nn, meanNormal)) < ANGLE_TOL) {
        allPlanar = false;
        break;
      }
    }
    if (allPlanar) {
      // Compute signed distance from origin
      const dist = vecDot(meanNormal, vertPositions[0]);
      return {
        type: 'plane',
        triangleIndices,
        positions,
        normals,
        normal: meanNormal,
        distance: dist,
      };
    }
  }

  // --- Try Sphere ---
  // Compute centroid of all vertices
  const centroid: Vec3 = [0, 0, 0];
  for (const p of vertPositions) {
    centroid[0] += p[0];
    centroid[1] += p[1];
    centroid[2] += p[2];
  }
  centroid[0] /= numVerts;
  centroid[1] /= numVerts;
  centroid[2] /= numVerts;

  const distances = vertPositions.map((p) => vecLen(vecSub(p, centroid)));
  const meanDist = distances.reduce((a, b) => a + b, 0) / distances.length;
  if (meanDist > 1e-8) {
    const variance = distances.reduce((a, d) => a + (d - meanDist) ** 2, 0) / distances.length;
    const relStdDev = Math.sqrt(variance) / meanDist;
    if (relStdDev < RADIUS_TOL) {
      // Check that normals point radially outward (or inward)
      let radialMatch = true;
      for (let i = 0; i < vertPositions.length; i++) {
        const radial = vecNormalize(vecSub(vertPositions[i], centroid));
        const nn = vecNormalize(vertNormals[i]);
        if (Math.abs(Math.abs(vecDot(radial, nn)) - 1) > 0.15) {
          radialMatch = false;
          break;
        }
      }
      if (radialMatch) {
        return {
          type: 'sphere',
          triangleIndices,
          positions,
          normals,
          center: centroid,
          radius: meanDist,
        };
      }
    }
  }

  // --- Try Cylinder ---
  // Find candidate axis from cross product of two most-different normals
  const cylinderResult = tryCylinder(vertPositions, vertNormals);
  if (cylinderResult) {
    return {
      type: 'cylinder',
      triangleIndices,
      positions,
      normals,
      axis: cylinderResult.axis,
      axisPoint: cylinderResult.axisPoint,
      radius: cylinderResult.radius,
    };
  }

  // --- Try Cone ---
  const coneResult = tryCone(vertPositions, vertNormals);
  if (coneResult) {
    return {
      type: 'cone',
      triangleIndices,
      positions,
      normals,
      axis: coneResult.axis,
      axisPoint: coneResult.axisPoint,
      radius: coneResult.radius,
      radius2: coneResult.radius2,
    };
  }

  return { type: 'unknown', triangleIndices, positions, normals };
}

function tryCylinder(
  verts: Vec3[],
  norms: Vec3[],
): { axis: Vec3; axisPoint: Vec3; radius: number } | null {
  if (verts.length < 6) return null;

  // Find two normals with maximum angular difference
  let maxAngle = 0;
  let n1: Vec3 = [0, 0, 0];
  let n2: Vec3 = [0, 0, 0];

  const sampleNormals = norms.length > 50
    ? norms.filter((_, i) => i % Math.ceil(norms.length / 50) === 0)
    : norms;

  for (let i = 0; i < sampleNormals.length; i++) {
    for (let j = i + 1; j < sampleNormals.length; j++) {
      const nn1 = vecNormalize(sampleNormals[i]);
      const nn2 = vecNormalize(sampleNormals[j]);
      const angle = 1 - Math.abs(vecDot(nn1, nn2));
      if (angle > maxAngle) {
        maxAngle = angle;
        n1 = nn1;
        n2 = nn2;
      }
    }
  }

  if (maxAngle < 0.01) return null; // normals too similar

  // Candidate axis = cross product of the two most different normals
  let axis = vecNormalize(vecCross(n1, n2));
  if (vecLen(axis) < 0.1) return null;

  // All normals must be perpendicular to axis
  for (const n of norms) {
    const nn = vecNormalize(n);
    if (vecLen(nn) < 0.1) continue;
    if (Math.abs(vecDot(nn, axis)) > 0.15) return null;
  }

  // Canonicalize axis direction (prefer positive Z, then Y, then X)
  if (axis[2] < -0.5 || (Math.abs(axis[2]) < 0.1 && axis[1] < -0.5) ||
      (Math.abs(axis[2]) < 0.1 && Math.abs(axis[1]) < 0.1 && axis[0] < -0.5)) {
    axis = vecScale(axis, -1);
  }

  // Project all vertices to 2D plane perpendicular to axis via circle fit.
  // This handles both full cylinders and partial arcs (half-cylinders from OCCT).
  const [u, v] = orthoBasis(axis);
  const pts2d: Array<[number, number]> = verts.map((p) => [vecDot(p, u), vecDot(p, v)]);
  const circle = fitCircle2D(pts2d);
  if (!circle) return null;

  // Convert 2D center back to 3D axis point
  const axisPoint: Vec3 = vecAdd(vecScale(u, circle.cx), vecScale(v, circle.cy));

  // Check radius consistency from fitted center
  const radii = pts2d.map(([x, y]) =>
    Math.sqrt((x - circle.cx) ** 2 + (y - circle.cy) ** 2),
  );
  const variance = radii.reduce((a, r) => a + (r - circle.r) ** 2, 0) / radii.length;
  const relStdDev = Math.sqrt(variance) / circle.r;
  if (relStdDev > RADIUS_TOL) return null;

  return { axis, axisPoint, radius: circle.r };
}

function tryCone(
  verts: Vec3[],
  norms: Vec3[],
): { axis: Vec3; axisPoint: Vec3; radius: number; radius2: number } | null {
  if (verts.length < 6) return null;

  // Find candidate axis from cross products, similar to cylinder
  let maxAngle = 0;
  let n1: Vec3 = [0, 0, 0];
  let n2: Vec3 = [0, 0, 0];

  const sampleNormals = norms.length > 50
    ? norms.filter((_, i) => i % Math.ceil(norms.length / 50) === 0)
    : norms;

  for (let i = 0; i < sampleNormals.length; i++) {
    for (let j = i + 1; j < sampleNormals.length; j++) {
      const nn1 = vecNormalize(sampleNormals[i]);
      const nn2 = vecNormalize(sampleNormals[j]);
      const angle = 1 - Math.abs(vecDot(nn1, nn2));
      if (angle > maxAngle) {
        maxAngle = angle;
        n1 = nn1;
        n2 = nn2;
      }
    }
  }

  if (maxAngle < 0.01) return null;

  let axis = vecNormalize(vecCross(n1, n2));
  if (vecLen(axis) < 0.1) return null;

  // For cone: normals are NOT perpendicular to axis but have a consistent
  // angle component along the axis. Check if the dot product with axis
  // is approximately constant (within tolerance).
  const dotProducts = norms.map((n) => Math.abs(vecDot(vecNormalize(n), axis)));
  const meanDot = dotProducts.reduce((a, b) => a + b, 0) / dotProducts.length;

  // For a cone, the normals should have a consistent (non-zero) angle with the axis
  if (meanDot < 0.05 || meanDot > 0.95) return null; // too close to cylinder or plane

  const dotVariance = dotProducts.reduce((a, d) => a + (d - meanDot) ** 2, 0) / dotProducts.length;
  if (Math.sqrt(dotVariance) > 0.1) return null;

  // Canonicalize axis direction
  if (axis[2] < -0.5 || (Math.abs(axis[2]) < 0.1 && axis[1] < -0.5)) {
    axis = vecScale(axis, -1);
  }

  // Project vertices along axis and measure radius at each height
  const heights = verts.map((p) => vecDot(p, axis));
  const minH = Math.min(...heights);
  const maxH = Math.max(...heights);
  if (maxH - minH < 1e-8) return null;

  // Compute axis point via circle fit on all vertices
  const [cu, cv] = orthoBasis(axis);
  const conePts2d: Array<[number, number]> = verts.map((p) => [vecDot(p, cu), vecDot(p, cv)]);
  const coneCircle = fitCircle2D(conePts2d);
  if (!coneCircle) return null;
  const axisPoint: Vec3 = vecAdd(vecScale(cu, coneCircle.cx), vecScale(cv, coneCircle.cy));

  // Find radius at min and max heights
  const bottomVerts = verts.filter((_, i) => Math.abs(heights[i] - minH) < (maxH - minH) * 0.2);
  const topVerts = verts.filter((_, i) => Math.abs(heights[i] - maxH) < (maxH - minH) * 0.2);

  if (bottomVerts.length < 3 || topVerts.length < 3) return null;

  const bottomRadii = bottomVerts.map((p) => {
    const proj = vecSub(p, vecScale(axis, vecDot(p, axis)));
    return vecLen(vecSub(proj, axisPoint));
  });
  const topRadii = topVerts.map((p) => {
    const proj = vecSub(p, vecScale(axis, vecDot(p, axis)));
    return vecLen(vecSub(proj, axisPoint));
  });

  const r1 = bottomRadii.reduce((a, b) => a + b, 0) / bottomRadii.length;
  const r2 = topRadii.reduce((a, b) => a + b, 0) / topRadii.length;

  // Radii should be measurably different (otherwise it's a cylinder)
  if (Math.abs(r1 - r2) < Math.max(r1, r2) * 0.05) return null;

  return { axis, axisPoint, radius: r1, radius2: r2 };
}

// ── Step 3: Assemble primitives ────────────────────────────────────────────────

function assemblePrimitives(faces: ClassifiedFace[]): RecognizedPrimitive[] {
  const primitives: RecognizedPrimitive[] = [];
  const used = new Set<number>();

  const planes = faces
    .map((f, i) => ({ face: f, idx: i }))
    .filter((f) => f.face.type === 'plane');

  const cylinders = faces
    .map((f, i) => ({ face: f, idx: i }))
    .filter((f) => f.face.type === 'cylinder');

  const spheres = faces
    .map((f, i) => ({ face: f, idx: i }))
    .filter((f) => f.face.type === 'sphere');

  const cones = faces
    .map((f, i) => ({ face: f, idx: i }))
    .filter((f) => f.face.type === 'cone');

  // --- Try to find L-shapes: 8 planes with 2 stepped axes + 1 uniform ---
  // Must run before boxes so it consumes all 8 planes (boxes would grab 6).
  tryAssembleLShapes(planes, used, primitives);

  // --- Try to find boxes: 3 pairs of opposing parallel planes ---
  tryAssembleBoxes(planes, used, primitives);

  // --- Try to find cylinders: 1 cylindrical face + 2 planar caps ---
  tryAssembleCylinders(cylinders, planes, used, primitives);

  // --- Try to find cones: 1 conical face + 2 planar caps ---
  tryAssembleCones(cones, planes, used, primitives);

  // --- Spheres: single spherical face ---
  for (const s of spheres) {
    if (used.has(s.idx)) continue;
    used.add(s.idx);
    primitives.push({
      type: 'sphere',
      faces: [s.face],
      position: s.face.center,
      radius: s.face.radius,
      isHole: false,
    });
  }

  // --- Merge split sphere patches (OCCT often splits spheres into hemispheres) ---
  mergeSpheres(primitives);

  // --- Standalone cylinders without caps ---
  for (const c of cylinders) {
    if (used.has(c.idx)) continue;
    used.add(c.idx);
    const verts = getVertPositions(c.face);
    const heights = verts.map((v) => vecDot(v, c.face.axis!));
    const minH = Math.min(...heights);
    const maxH = Math.max(...heights);
    const midH = (minH + maxH) / 2;
    const centerOnAxis = vecAdd(c.face.axisPoint!, vecScale(c.face.axis!, midH));

    const cylAxis = c.face.axis!;
    primitives.push({
      type: 'cylinder',
      faces: [c.face],
      position: centerOnAxis,
      height: maxH - minH,
      radius: c.face.radius,
      axis: cylAxis,
      rotation: axisToRotation(cylAxis) ?? undefined,
      isHole: false,
    });
  }

  // --- Merge coaxial half-cylinders (OCCT splits cylindrical holes into 2 halves) ---
  mergeCoaxialCylinders(primitives);

  // --- Unknown faces: count them for reporting but don't emit polyhedron ---
  // B-rep faces are open surface patches, not closed solids.
  // Emitting them as polyhedron() produces non-manifold errors.
  let unknownCount = 0;
  for (let i = 0; i < faces.length; i++) {
    if (!used.has(i)) unknownCount++;
  }
  if (unknownCount > 0) {
    primitives.push({
      type: 'unknown',
      faces: [],
      isHole: false,
      unknownFaceCount: unknownCount,
    });
  }

  return primitives;
}

function mergeCoaxialCylinders(primitives: RecognizedPrimitive[]): void {
  const cylIndices: number[] = [];
  for (let i = 0; i < primitives.length; i++) {
    if (primitives[i].type === 'cylinder') cylIndices.push(i);
  }
  if (cylIndices.length < 2) return;

  const merged = new Set<number>();
  for (let i = 0; i < cylIndices.length; i++) {
    if (merged.has(i)) continue;
    const a = primitives[cylIndices[i]];

    for (let j = i + 1; j < cylIndices.length; j++) {
      if (merged.has(j)) continue;
      const b = primitives[cylIndices[j]];

      const axisA = a.axis ?? [0, 0, 1] as Vec3;
      const axisB = b.axis ?? [0, 0, 1] as Vec3;

      // Axes must be parallel
      if (Math.abs(Math.abs(vecDot(axisA, axisB)) - 1) > 0.15) continue;

      // Radii must match (within 5%)
      const avgR = (a.radius! + b.radius!) / 2;
      if (Math.abs(a.radius! - b.radius!) / avgR > 0.05) continue;

      // Axis lines must nearly coincide (perpendicular distance < 30% of radius)
      const relPos = vecSub(a.position!, b.position!);
      const along = vecDot(relPos, axisA);
      const perpVec = vecSub(relPos, vecScale(axisA, along));
      const perpDist = vecLen(perpVec);
      if (perpDist > avgR * 0.3) continue;

      // Heights must overlap by at least 50%
      const aHalfH = a.height! / 2;
      const bHalfH = b.height! / 2;
      const aH = vecDot(a.position!, axisA);
      const bH = vecDot(b.position!, axisA);
      const overlapMin = Math.max(aH - aHalfH, bH - bHalfH);
      const overlapMax = Math.min(aH + aHalfH, bH + bHalfH);
      if (overlapMax - overlapMin < Math.min(a.height!, b.height!) * 0.5) continue;

      // Merge: keep a, absorb b
      a.faces.push(...b.faces);
      a.radius = avgR;
      // Average position
      a.position = [
        (a.position![0] + b.position![0]) / 2,
        (a.position![1] + b.position![1]) / 2,
        (a.position![2] + b.position![2]) / 2,
      ];
      // Union of height ranges
      const unionMin = Math.min(aH - aHalfH, bH - bHalfH);
      const unionMax = Math.max(aH + aHalfH, bH + bHalfH);
      a.height = unionMax - unionMin;
      merged.add(j);
    }
  }

  // Remove merged cylinders (reverse order to preserve indices)
  const toRemove = [...merged].map((j) => cylIndices[j]).sort((a, b) => b - a);
  for (const idx of toRemove) {
    primitives.splice(idx, 1);
  }
}

function mergeSpheres(primitives: RecognizedPrimitive[]): void {
  const sphereIndices: number[] = [];
  for (let i = 0; i < primitives.length; i++) {
    if (primitives[i].type === 'sphere') sphereIndices.push(i);
  }
  if (sphereIndices.length < 2) return;

  const merged = new Set<number>();
  for (let i = 0; i < sphereIndices.length; i++) {
    if (merged.has(i)) continue;
    const si = sphereIndices[i];
    const a = primitives[si];

    for (let j = i + 1; j < sphereIndices.length; j++) {
      if (merged.has(j)) continue;
      const sj = sphereIndices[j];
      const b = primitives[sj];

      const centerDist = vecLen(vecSub(a.position!, b.position!));
      const avgRadius = (a.radius! + b.radius!) / 2;
      const radiusDiff = Math.abs(a.radius! - b.radius!) / avgRadius;

      // Merge if centers are close (< 10% of radius) and radii match (< 2%)
      if (centerDist < avgRadius * 0.1 && radiusDiff < 0.02) {
        // Keep the one with more faces, absorb the other
        a.faces.push(...b.faces);
        a.radius = avgRadius;
        // Average the centers
        a.position = [
          (a.position![0] + b.position![0]) / 2,
          (a.position![1] + b.position![1]) / 2,
          (a.position![2] + b.position![2]) / 2,
        ];
        merged.add(j);
      }
    }
  }

  // Remove merged spheres (iterate in reverse to preserve indices)
  const toRemove = [...merged].map((j) => sphereIndices[j]).sort((a, b) => b - a);
  for (const idx of toRemove) {
    primitives.splice(idx, 1);
  }
}

function getVertPositions(face: ClassifiedFace): Vec3[] {
  const result: Vec3[] = [];
  for (let i = 0; i < face.positions.length; i += 3) {
    result.push([face.positions[i], face.positions[i + 1], face.positions[i + 2]]);
  }
  return result;
}

function tryAssembleBoxes(
  planes: Array<{ face: ClassifiedFace; idx: number }>,
  used: Set<number>,
  primitives: RecognizedPrimitive[],
): void {
  // Group planes by opposing normals
  const availablePlanes = planes.filter((p) => !used.has(p.idx));

  // Find pairs with opposing normals (dot product close to -1)
  const pairs: Array<[typeof availablePlanes[0], typeof availablePlanes[0]]> = [];
  const pairedSet = new Set<number>();

  for (let i = 0; i < availablePlanes.length; i++) {
    if (pairedSet.has(i)) continue;
    for (let j = i + 1; j < availablePlanes.length; j++) {
      if (pairedSet.has(j)) continue;
      const n1 = availablePlanes[i].face.normal!;
      const n2 = availablePlanes[j].face.normal!;
      if (vecDot(n1, n2) < -ANGLE_TOL) {
        pairs.push([availablePlanes[i], availablePlanes[j]]);
        pairedSet.add(i);
        pairedSet.add(j);
        break;
      }
    }
  }

  if (pairs.length < 3) return; // Need at least 3 pairs for a box

  // Try all combinations of 3 pairs
  for (let a = 0; a < pairs.length - 2; a++) {
    for (let b = a + 1; b < pairs.length - 1; b++) {
      for (let c = b + 1; c < pairs.length; c++) {
        const trio = [pairs[a], pairs[b], pairs[c]];

        // Check that the 3 pair normals are mutually perpendicular
        const normals = trio.map((pair) => pair[0].face.normal!);
        const d01 = Math.abs(vecDot(normals[0], normals[1]));
        const d02 = Math.abs(vecDot(normals[0], normals[2]));
        const d12 = Math.abs(vecDot(normals[1], normals[2]));

        if (d01 > 0.15 || d02 > 0.15 || d12 > 0.15) continue;

        // Check that all 6 faces are unused
        const indices = trio.flatMap((pair) => [pair[0].idx, pair[1].idx]);
        if (indices.some((i) => used.has(i))) continue;

        // Compute box dimensions from plane-to-plane distances
        const sizes: Vec3 = [0, 0, 0];
        const centers: Vec3 = [0, 0, 0];
        for (let i = 0; i < 3; i++) {
          const d1 = trio[i][0].face.distance!;
          const d2 = trio[i][1].face.distance!;
          // The normals point in opposite directions, so the distances
          // have opposite signs relative to the plane pair
          sizes[i] = Math.abs(d1 + d2); // d2 is along negated normal
          centers[i] = (d1 - d2) / 2;   // midpoint along normal direction
        }

        // Reconstruct the center position in world space
        // Each center[i] is a coordinate along normals[i]
        const position: Vec3 = [0, 0, 0];
        for (let i = 0; i < 3; i++) {
          position[0] += normals[i][0] * centers[i];
          position[1] += normals[i][1] * centers[i];
          position[2] += normals[i][2] * centers[i];
        }

        // Check if box is axis-aligned
        const axisAligned = normals.every((n) => {
          const absN = n.map(Math.abs);
          return absN.filter((v) => v > 0.9).length === 1;
        });

        // Mark faces as used
        for (const idx of indices) used.add(idx);

        if (axisAligned) {
          // Map normals to XYZ axes to get proper [width, depth, height]
          const sizeXYZ: Vec3 = [0, 0, 0];
          const centerXYZ: Vec3 = [0, 0, 0];
          for (let i = 0; i < 3; i++) {
            const absN = normals[i].map(Math.abs);
            const axisIdx = absN.indexOf(Math.max(...absN));
            sizeXYZ[axisIdx] = sizes[i];
            centerXYZ[axisIdx] = centers[i] * Math.sign(normals[i][axisIdx]);
          }

          primitives.push({
            type: 'box',
            faces: trio.flatMap((pair) => [pair[0].face, pair[1].face]),
            size: sizeXYZ,
            position: centerXYZ,
            isHole: false,
          });
        } else {
          // Rotated box: build rotation from the 3 face normals
          // Columns = local X, Y, Z (the face normal directions)
          const cols: [Vec3, Vec3, Vec3] = [
            vecNormalize(normals[0]),
            vecNormalize(normals[1]),
            vecNormalize(normals[2]),
          ];

          // Ensure right-handed coordinate system
          const cross = vecCross(cols[0], cols[1]);
          const det = vecDot(cross, cols[2]);
          if (det < 0) cols[2] = vecScale(cols[2], -1);

          const rotation = matrixToEulerZYX(cols);

          // sizes[i] corresponds to normals[i] which is cols[i], so
          // sizes are already in local-frame order [localX, localY, localZ]
          primitives.push({
            type: 'box',
            faces: trio.flatMap((pair) => [pair[0].face, pair[1].face]),
            size: [sizes[0], sizes[1], sizes[2]],
            position,
            rotation,
            isHole: false,
          });
        }
      }
    }
  }
}

function tryAssembleLShapes(
  planes: Array<{ face: ClassifiedFace; idx: number }>,
  used: Set<number>,
  primitives: RecognizedPrimitive[],
): void {
  const available = planes.filter((p) => !used.has(p.idx));
  if (available.length < 8) return;

  // Group planes by which world axis they align with (axis-aligned only)
  // groups[axisIdx][sign] = array of { plane, coordinate }
  // sign: 0 = negative normal, 1 = positive normal
  type PlaneInfo = { plane: typeof available[0]; coord: number };
  const groups: [PlaneInfo[], PlaneInfo[]][] = [
    [[], []], // X axis: [neg, pos]
    [[], []], // Y axis
    [[], []], // Z axis
  ];

  for (const p of available) {
    const n = p.face.normal!;
    const absN = n.map(Math.abs);
    const maxVal = Math.max(...absN);
    if (maxVal < 0.9) continue; // not axis-aligned
    const axisIdx = absN.indexOf(maxVal);
    const sign = n[axisIdx] > 0 ? 1 : 0;
    // Coordinate on this axis: for +normal, coord = distance; for -normal, coord = -distance
    const coord = sign === 1 ? p.face.distance! : -p.face.distance!;
    groups[axisIdx][sign].push({ plane: p, coord });
  }

  // Look for L-shape pattern: exactly 2 stepped axes (1+2) and 1 uniform axis (1+1)
  const axisInfo: Array<{
    axisIdx: number;
    negPlanes: PlaneInfo[];
    posPlanes: PlaneInfo[];
    isStepped: boolean;
    totalPlanes: number;
  }> = [];

  for (let ai = 0; ai < 3; ai++) {
    const neg = groups[ai][0];
    const pos = groups[ai][1];
    const total = neg.length + pos.length;
    const stepped = (neg.length === 1 && pos.length === 2) || (neg.length === 2 && pos.length === 1);
    const uniform = neg.length === 1 && pos.length === 1;
    if (total >= 2 && (stepped || uniform)) {
      axisInfo.push({ axisIdx: ai, negPlanes: neg, posPlanes: pos, isStepped: stepped, totalPlanes: total });
    }
  }

  if (axisInfo.length !== 3) return;
  const stepped = axisInfo.filter((a) => a.isStepped);
  const uniform = axisInfo.filter((a) => !a.isStepped);
  if (stepped.length !== 2 || uniform.length !== 1) return;

  // Compute outer box and notch box
  const outerMin: Vec3 = [0, 0, 0];
  const outerMax: Vec3 = [0, 0, 0];
  const notchMin: Vec3 = [0, 0, 0];
  const notchMax: Vec3 = [0, 0, 0];
  const allPlaneEntries: typeof available = [];

  for (const info of axisInfo) {
    const ai = info.axisIdx;
    const allCoords = [...info.negPlanes, ...info.posPlanes].map((p) => p.coord);
    outerMin[ai] = Math.min(...allCoords);
    outerMax[ai] = Math.max(...allCoords);

    if (info.isStepped) {
      // The stepped side has 2 planes. The "inner" plane creates the notch boundary.
      if (info.posPlanes.length === 2) {
        // Two + planes: inner is the one with smaller coord (closer to neg side)
        const sorted = [...info.posPlanes].sort((a, b) => a.coord - b.coord);
        notchMin[ai] = sorted[0].coord; // inner plane
        notchMax[ai] = sorted[1].coord; // outer plane (= outerMax)
      } else {
        // Two - planes: inner is the one with larger coord (closer to pos side)
        const sorted = [...info.negPlanes].sort((a, b) => a.coord - b.coord);
        notchMin[ai] = sorted[0].coord; // outer plane (= outerMin)
        notchMax[ai] = sorted[1].coord; // inner plane
      }
    } else {
      // Uniform axis: notch spans the full range
      notchMin[ai] = outerMin[ai];
      notchMax[ai] = outerMax[ai];
    }

    for (const pi of [...info.negPlanes, ...info.posPlanes]) {
      allPlaneEntries.push(pi.plane);
    }
  }

  // Validate: notch must be strictly inside outer box (not equal)
  for (let i = 0; i < 3; i++) {
    if (notchMin[i] < outerMin[i] - 0.01 || notchMax[i] > outerMax[i] + 0.01) return;
  }
  const notchVol =
    (notchMax[0] - notchMin[0]) * (notchMax[1] - notchMin[1]) * (notchMax[2] - notchMin[2]);
  const outerVol =
    (outerMax[0] - outerMin[0]) * (outerMax[1] - outerMin[1]) * (outerMax[2] - outerMin[2]);
  if (notchVol <= 0 || notchVol >= outerVol * 0.99) return; // notch too small or too large

  // Mark all 8 planes as used
  for (const p of allPlaneEntries) used.add(p.idx);

  const outerSize: Vec3 = [
    outerMax[0] - outerMin[0],
    outerMax[1] - outerMin[1],
    outerMax[2] - outerMin[2],
  ];
  const outerCenter: Vec3 = [
    (outerMin[0] + outerMax[0]) / 2,
    (outerMin[1] + outerMax[1]) / 2,
    (outerMin[2] + outerMax[2]) / 2,
  ];
  const notchSize: Vec3 = [
    notchMax[0] - notchMin[0],
    notchMax[1] - notchMin[1],
    notchMax[2] - notchMin[2],
  ];
  const notchCenter: Vec3 = [
    (notchMin[0] + notchMax[0]) / 2,
    (notchMin[1] + notchMax[1]) / 2,
    (notchMin[2] + notchMax[2]) / 2,
  ];

  primitives.push({
    type: 'lshape',
    faces: allPlaneEntries.map((p) => p.face),
    size: outerSize,
    position: outerCenter,
    notchSize,
    notchPosition: notchCenter,
    isHole: false,
  });
}

function tryAssembleCylinders(
  cylinders: Array<{ face: ClassifiedFace; idx: number }>,
  planes: Array<{ face: ClassifiedFace; idx: number }>,
  used: Set<number>,
  primitives: RecognizedPrimitive[],
): void {
  for (const cyl of cylinders) {
    if (used.has(cyl.idx)) continue;

    const axis = cyl.face.axis!;
    const radius = cyl.face.radius!;

    // Find vertices along the axis to determine height range
    const verts = getVertPositions(cyl.face);
    const heights = verts.map((v) => vecDot(v, axis));
    const minH = Math.min(...heights);
    const maxH = Math.max(...heights);

    // Look for 2 planar caps perpendicular to the cylinder axis
    const caps: Array<typeof planes[0]> = [];
    for (const plane of planes) {
      if (used.has(plane.idx)) continue;
      const normal = plane.face.normal!;
      // Cap must be perpendicular to cylinder axis
      if (Math.abs(Math.abs(vecDot(normal, axis)) - 1) > 0.15) continue;

      // Cap must be at one of the cylinder ends
      const capDist = plane.face.distance!;
      const capH = capDist / vecDot(normal, axis);
      if (Math.abs(capH - minH) < (maxH - minH) * 0.1 + 0.01 ||
          Math.abs(capH - maxH) < (maxH - minH) * 0.1 + 0.01) {
        caps.push(plane);
        if (caps.length === 2) break;
      }
    }

    const height = maxH - minH;
    const midH = (minH + maxH) / 2;
    const centerOnAxis = vecAdd(cyl.face.axisPoint!, vecScale(axis, midH));

    used.add(cyl.idx);
    for (const cap of caps) used.add(cap.idx);

    primitives.push({
      type: 'cylinder',
      faces: [cyl.face, ...caps.map((c) => c.face)],
      position: centerOnAxis,
      height,
      radius,
      axis,
      rotation: axisToRotation(axis) ?? undefined,
      isHole: false,
    });
  }
}

function tryAssembleCones(
  cones: Array<{ face: ClassifiedFace; idx: number }>,
  planes: Array<{ face: ClassifiedFace; idx: number }>,
  used: Set<number>,
  primitives: RecognizedPrimitive[],
): void {
  for (const cone of cones) {
    if (used.has(cone.idx)) continue;

    const axis = cone.face.axis!;
    const r1 = cone.face.radius!;
    const r2 = cone.face.radius2!;

    const verts = getVertPositions(cone.face);
    const heights = verts.map((v) => vecDot(v, axis));
    const minH = Math.min(...heights);
    const maxH = Math.max(...heights);

    // Find caps
    const caps: Array<typeof planes[0]> = [];
    for (const plane of planes) {
      if (used.has(plane.idx)) continue;
      const normal = plane.face.normal!;
      if (Math.abs(Math.abs(vecDot(normal, axis)) - 1) > 0.15) continue;
      caps.push(plane);
      if (caps.length === 2) break;
    }

    const height = maxH - minH;
    const midH = (minH + maxH) / 2;
    const centerOnAxis = vecAdd(cone.face.axisPoint!, vecScale(axis, midH));

    used.add(cone.idx);
    for (const cap of caps) used.add(cap.idx);

    primitives.push({
      type: 'cone',
      faces: [cone.face, ...caps.map((c) => c.face)],
      position: centerOnAxis,
      height,
      radius: r1,
      radius2: r2,
      axis,
      rotation: axisToRotation(axis) ?? undefined,
      isHole: false,
    });
  }
}

// ── Step 4: Detect holes ───────────────────────────────────────────────────────

function detectHoles(primitives: RecognizedPrimitive[]): void {
  // Collect all box-like solids (boxes and L-shapes use their outer bounding box)
  const boxLike = primitives.filter((p) => p.type === 'box' || p.type === 'lshape');
  const cyls = primitives.filter((p) => p.type === 'cylinder');

  // --- Cylinder-in-box/lshape holes ---
  for (const cyl of cyls) {
    if (cyl.isHole) continue;
    if (!cyl.position || !cyl.radius || !cyl.height) continue;

    const cylAxis = cyl.axis ?? [0, 0, 1] as Vec3;
    const cp = cyl.position;
    const cr = cyl.radius;

    for (const box of boxLike) {
      if (!box.position || !box.size) continue;

      const bp = box.position;
      const bs = box.size;

      // Determine which box axis the cylinder aligns with
      const axes: Vec3[] = [[1, 0, 0], [0, 1, 0], [0, 0, 1]];
      // If box has rotation, use its actual local axes
      let boxAxes = axes;
      if (box.rotation) {
        const cols = eulerToAxes(box.rotation);
        if (cols) boxAxes = cols;
      }

      let matched = false;
      for (let ai = 0; ai < 3; ai++) {
        const boxAxis = boxAxes[ai];
        const absDotVal = Math.abs(vecDot(cylAxis, boxAxis));
        if (absDotVal < 0.85) continue; // cylinder not aligned with this box axis

        // Check containment perpendicular to this axis
        // Project cylinder center relative to box center onto perpendicular axes
        const relPos = vecSub(cp, bp);
        let contained = true;
        let fitsFace = true;
        for (let pi = 0; pi < 3; pi++) {
          if (pi === ai) continue;
          const perpAxis = boxAxes[pi];
          const projDist = Math.abs(vecDot(relPos, perpAxis));
          const halfSize = bs[pi] / 2;
          if (projDist > halfSize + cr * 0.1) { contained = false; break; }
          if (cr >= halfSize) { fitsFace = false; break; }
        }

        if (contained && fitsFace) {
          cyl.isHole = true;
          cyl.height = bs[ai] + 1;
          matched = true;
          break;
        }
      }
      if (matched) break;
    }
  }

  // --- Cylinder-in-cylinder holes (concentric tubes) ---
  for (let i = 0; i < cyls.length; i++) {
    const inner = cyls[i];
    if (inner.isHole) continue;
    if (!inner.position || !inner.radius || !inner.height) continue;

    const innerAxis = inner.axis ?? [0, 0, 1] as Vec3;

    for (let j = 0; j < cyls.length; j++) {
      if (i === j) continue;
      const outer = cyls[j];
      if (!outer.position || !outer.radius || !outer.height) continue;
      if (outer.radius! <= inner.radius!) continue; // outer must be bigger

      const outerAxis = outer.axis ?? [0, 0, 1] as Vec3;

      // Check axes are parallel
      const dotVal = Math.abs(vecDot(innerAxis, outerAxis));
      if (dotVal < 0.85) continue;

      // Check axis lines nearly coincide (perpendicular distance)
      const relPos = vecSub(inner.position, outer.position);
      const along = vecDot(relPos, outerAxis);
      const perpVec = vecSub(relPos, vecScale(outerAxis, along));
      const perpDist = vecLen(perpVec);
      if (perpDist > inner.radius! * 0.3) continue;

      // Check height ranges overlap by at least 50% of the shorter cylinder
      const innerHalfH = inner.height! / 2;
      const outerHalfH = outer.height! / 2;
      const innerMin = vecDot(inner.position, outerAxis) - innerHalfH;
      const innerMax = vecDot(inner.position, outerAxis) + innerHalfH;
      const outerMin = vecDot(outer.position, outerAxis) - outerHalfH;
      const outerMax = vecDot(outer.position, outerAxis) + outerHalfH;
      const overlapMin = Math.max(innerMin, outerMin);
      const overlapMax = Math.min(innerMax, outerMax);
      const overlap = overlapMax - overlapMin;
      const minH = Math.min(inner.height!, outer.height!);
      if (overlap < minH * 0.5) continue;

      // Mark inner as hole; keep the larger of bore height or outer+1
      inner.isHole = true;
      inner.height = Math.max(inner.height!, outer.height! + 1);
      break;
    }
  }

  // --- Off-axis cylinder holes (bolt holes through larger cylinder face) ---
  for (let i = 0; i < cyls.length; i++) {
    const inner = cyls[i];
    if (inner.isHole) continue;
    if (!inner.position || !inner.radius || !inner.height) continue;

    const innerAxis = inner.axis ?? [0, 0, 1] as Vec3;

    for (let j = 0; j < cyls.length; j++) {
      if (i === j) continue;
      const outer = cyls[j];
      if (outer.isHole) continue;
      if (!outer.position || !outer.radius || !outer.height) continue;
      if (outer.radius! <= inner.radius!) continue;

      const outerAxis = outer.axis ?? [0, 0, 1] as Vec3;

      // Axes must be parallel
      const dotVal = Math.abs(vecDot(innerAxis, outerAxis));
      if (dotVal < 0.85) continue;

      // Inner cylinder center must fall within outer's cross-section
      const relPos = vecSub(inner.position, outer.position);
      const along = vecDot(relPos, outerAxis);
      const perpVec = vecSub(relPos, vecScale(outerAxis, along));
      const perpDist = vecLen(perpVec);
      // Inner circle (center + radius) must fit inside outer radius
      if (perpDist + inner.radius! > outer.radius! * 1.05) continue;

      // Inner must not be taller than outer
      if (inner.height! > outer.height! * 1.5) continue;

      // Height ranges must overlap substantially
      const innerHalfH = inner.height! / 2;
      const outerHalfH = outer.height! / 2;
      const innerMin = vecDot(inner.position, outerAxis) - innerHalfH;
      const innerMax = vecDot(inner.position, outerAxis) + innerHalfH;
      const outerMin = vecDot(outer.position, outerAxis) - outerHalfH;
      const outerMax = vecDot(outer.position, outerAxis) + outerHalfH;
      const overlapMin = Math.max(innerMin, outerMin);
      const overlapMax = Math.min(innerMax, outerMax);
      const overlap = overlapMax - overlapMin;
      if (overlap < inner.height! * 0.5) continue;

      // Mark as hole, extend height past outer
      inner.isHole = true;
      inner.height = outer.height! + 1;
      break;
    }
  }
}

/** Convert ZYX Euler angles back to axis columns (inverse of matrixToEulerZYX) */
function eulerToAxes(rot: Vec3): [Vec3, Vec3, Vec3] | null {
  const [rx, ry, rz] = rot.map((d) => d * Math.PI / 180);
  const cx = Math.cos(rx), sx = Math.sin(rx);
  const cy = Math.cos(ry), sy = Math.sin(ry);
  const cz = Math.cos(rz), sz = Math.sin(rz);
  // ZYX rotation matrix columns
  const col0: Vec3 = [cy * cz, cy * sz, -sy];
  const col1: Vec3 = [sx * sy * cz - cx * sz, sx * sy * sz + cx * cz, sx * cy];
  const col2: Vec3 = [cx * sy * cz + sx * sz, cx * sy * sz - sx * cz, cx * cy];
  return [col0, col1, col2];
}

// ── Step 5: Generate SCAD ──────────────────────────────────────────────────────

function fmt(n: number): string {
  // Format number to reasonable precision, strip trailing zeros
  const s = n.toFixed(2);
  return s.replace(/\.?0+$/, '') || '0';
}

function primitiveToScad(prim: RecognizedPrimitive, indent: string): string {
  const pos = prim.position;
  const rot = prim.rotation;
  const lines: string[] = [];

  if (prim.type === 'unknown') {
    const count = prim.unknownFaceCount ?? 0;
    lines.push(`${indent}// ${count} B-rep face(s) not recognized (fillets, chamfers, freeform surfaces)`);
    lines.push(`${indent}// Use "Import STEP" for full fidelity of unrecognized geometry`);
    return lines.join('\n');
  }

  // L-shape: difference of outer box and notch (handles its own transforms)
  if (prim.type === 'lshape') {
    const s = prim.size!;
    const ns = prim.notchSize!;
    const np = prim.notchPosition!;
    lines.push(`${indent}difference() {`);
    const hasT = pos && (pos[0] !== 0 || pos[1] !== 0 || pos[2] !== 0);
    if (hasT) {
      lines.push(`${indent}  translate([${fmt(pos![0])}, ${fmt(pos![1])}, ${fmt(pos![2])}])`);
      lines.push(`${indent}    cube([${fmt(s[0])}, ${fmt(s[1])}, ${fmt(s[2])}], center = true);`);
    } else {
      lines.push(`${indent}  cube([${fmt(s[0])}, ${fmt(s[1])}, ${fmt(s[2])}], center = true);`);
    }
    const hasNT = np[0] !== 0 || np[1] !== 0 || np[2] !== 0;
    if (hasNT) {
      lines.push(`${indent}  translate([${fmt(np[0])}, ${fmt(np[1])}, ${fmt(np[2])}])`);
      lines.push(`${indent}    cube([${fmt(ns[0])}, ${fmt(ns[1])}, ${fmt(ns[2])}], center = true);`);
    } else {
      lines.push(`${indent}  cube([${fmt(ns[0])}, ${fmt(ns[1])}, ${fmt(ns[2])}], center = true);`);
    }
    lines.push(`${indent}}`);
    return lines.join('\n');
  }

  // Build transform prefix: translate then rotate (applied outer-to-inner)
  const hasTranslate = pos && (pos[0] !== 0 || pos[1] !== 0 || pos[2] !== 0);
  const hasRotate = rot && (Math.abs(rot[0]) > 0.01 || Math.abs(rot[1]) > 0.01 || Math.abs(rot[2]) > 0.01);
  let depth = indent;

  if (hasTranslate) {
    lines.push(`${depth}translate([${fmt(pos![0])}, ${fmt(pos![1])}, ${fmt(pos![2])}])`);
    depth += '  ';
  }
  if (hasRotate) {
    lines.push(`${depth}rotate([${fmt(rot![0])}, ${fmt(rot![1])}, ${fmt(rot![2])}])`);
    depth += '  ';
  }

  switch (prim.type) {
    case 'box': {
      const s = prim.size!;
      lines.push(`${depth}cube([${fmt(s[0])}, ${fmt(s[1])}, ${fmt(s[2])}], center = true);`);
      break;
    }
    case 'cylinder': {
      const r = prim.radius!;
      const h = prim.height!;
      lines.push(`${depth}cylinder(h = ${fmt(h)}, r = ${fmt(r)}, center = true);`);
      break;
    }
    case 'cone': {
      const r1 = prim.radius!;
      const r2 = prim.radius2!;
      const h = prim.height!;
      lines.push(`${depth}cylinder(h = ${fmt(h)}, r1 = ${fmt(r1)}, r2 = ${fmt(r2)}, center = true);`);
      break;
    }
    case 'sphere': {
      const r = prim.radius!;
      lines.push(`${depth}sphere(r = ${fmt(r)});`);
      break;
    }
  }

  return lines.join('\n');
}

function buildScad(primitives: RecognizedPrimitive[]): string {
  if (primitives.length === 0) return '// No geometry recognized\n';

  const holes = primitives.filter((p) => p.isHole);
  const solids = primitives.filter((p) => !p.isHole && p.type !== 'unknown');
  const unknowns = primitives.filter((p) => p.type === 'unknown');

  const lines: string[] = [];
  lines.push('// Generated from STEP file — primitive recognition');
  lines.push('');

  const hasHoles = holes.length > 0;
  const hasSolids = solids.length > 0;

  if (hasHoles && hasSolids) {
    lines.push('difference() {');

    if (solids.length === 1) {
      lines.push(primitiveToScad(solids[0], '  '));
    } else {
      lines.push('  union() {');
      for (const prim of solids) {
        lines.push(primitiveToScad(prim, '    '));
      }
      lines.push('  }');
    }

    for (const hole of holes) {
      lines.push(primitiveToScad(hole, '  '));
    }

    lines.push('}');
  } else if (solids.length > 1) {
    lines.push('union() {');
    for (const prim of solids) {
      lines.push(primitiveToScad(prim, '  '));
    }
    lines.push('}');
  } else if (solids.length === 1) {
    lines.push(primitiveToScad(solids[0], ''));
  }

  // Summarize unknown geometry (consolidate across meshes)
  const totalUnknown = unknowns.reduce((sum, u) => sum + (u.unknownFaceCount ?? 0), 0);
  if (totalUnknown > 0) {
    if (lines.length > 2) lines.push('');
    lines.push(`// ${totalUnknown} B-rep face(s) not recognized (fillets, chamfers, freeform surfaces)`);
    lines.push('// Use "Import STEP" for full fidelity of unrecognized geometry');
  }

  lines.push('');
  return lines.join('\n');
}

// ── Public API ─────────────────────────────────────────────────────────────────

export function stepMeshesToScad(
  meshes: Array<{
    name: string;
    color?: [number, number, number];
    attributes: { position: { array: number[] }; normal?: { array: number[] } };
    index: { array: number[] };
    brep_faces: Array<{ first: number; last: number; color: [number, number, number] | null }>;
  }>,
): { scad: string; recognized: number; total: number } {
  let totalFaces = 0;
  let recognizedFaces = 0;
  const allPrimitives: RecognizedPrimitive[] = [];

  // Process each mesh independently to avoid cross-mesh face mismatching
  for (const mesh of meshes) {
    const meshFaces: ClassifiedFace[] = [];

    if (!mesh.brep_faces || mesh.brep_faces.length === 0) {
      const { positions, normals, triangleIndices } = extractFaceGeometry(mesh, {
        first: 0,
        last: Math.floor(mesh.index.array.length / 3) - 1,
      });
      meshFaces.push(classifyFace(positions, normals, triangleIndices));
    } else {
      for (const face of mesh.brep_faces) {
        const { positions, normals, triangleIndices } = extractFaceGeometry(mesh, face);
        if (positions.length < 9) continue;
        meshFaces.push(classifyFace(positions, normals, triangleIndices));
      }
    }

    totalFaces += meshFaces.length;
    recognizedFaces += meshFaces.filter((f) => f.type !== 'unknown').length;

    const primitives = assemblePrimitives(meshFaces);
    detectHoles(primitives);
    allPrimitives.push(...primitives);
  }

  const scad = buildScad(allPrimitives);
  return { scad, recognized: recognizedFaces, total: totalFaces };
}
